import { Component, HostBinding, ViewChild, ViewEncapsulation, OnInit, ViewChildren, QueryList, ChangeDetectorRef, AfterViewChecked } from '@angular/core';
import { NgForm } from '@angular/forms';
import * as _ from 'underscore';
import { Endpoint, HelpIndex, ReviewSystem, SettingsType } from '../../../../models/enums';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { BaseModalComponent } from '../../../../models/base-modal-component';
import { ScrollablePopoverDirective } from '../../../../directives/scrollable-popover/scrollable-popover.directive';
import { ReefReviewConnector, ReefReviewConnectorViewModel } from '../../../../models/reef-review-connector';
import { ActionType, ObjectActionListType, Permissions } from '../../../../models/permission';

@Component({
    standalone: false,
    selector: 'res-review-system',
    templateUrl: './review-system.component.html',
    styleUrls: ['./review-system.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class SystemReviewComponent extends BaseModalComponent implements SettingsNavigationContentComponent, OnInit, AfterViewChecked {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild('contentForm', { static: true }) contentForm: NgForm;
    @ViewChildren(ScrollablePopoverDirective) errorPopovers: QueryList<ScrollablePopoverDirective>;

    isBusy = false;
    isBusyMessage: string;
    validChanges = true;
    helpIndex: HelpIndex;
    settings: ReefReviewConnector;
    viewModel: ReefReviewConnectorViewModel;
    hostUrl = { errorMessage: 'This field cannot be blank', invalid: false, invalidURL: false };
    remoteHost = { errorMessage: 'This field cannot be blank', invalid: false, invalidIP: false };
    remotePath = { errorMessage: 'This field cannot be blank', invalid: false, invalidPath: false };
    hasCreatePermissions: boolean;
    handle: string;
    isEdit = false;
    isEditRelativity = false;
    connectorValid = false;
    relativityConnectorValid = false;
    relativityHostUrl: string;
    relativityHandle: string;
    relativityUrl = { errorMessage: 'This field cannot be blank', invalid: false, invalidURL: false };

    get isDirty(): boolean {
        if (this.contentForm) {
            return this.contentForm.dirty;
        }
        return false;
    }
    set isDirty(value: boolean) {
        if (this.contentForm && this.contentForm.control) {
            if (value) {
                this.contentForm.control.markAsDirty();
            } else {
                this.contentForm.control.markAsPristine();
            }
        }
    }

    constructor(private commonService: CommonService, private restService: RestService, private modalService: BsModalService,
        private modalRef: BsModalRef, private changeDetector: ChangeDetectorRef) {
        super(modalService, modalRef);
    }

    ngOnInit() {
        this.viewModel = new ReefReviewConnectorViewModel(new ReefReviewConnector); // to get page to load. Will overwrite when load.
        this.hasCreatePermissions = Permissions.checkUserPermission(ObjectActionListType.REEF_REVIEW_CONNECTOR, ActionType.CREATE);
        this.loadSettingsData();
    }

    ngAfterViewChecked() {
        this.changeDetector.detectChanges();
    }

    loadSettingsData() {
        this.isBusyMessage = 'Loading Review Connector Configuration ...';
        this.isBusy = true;
        this.isDirty = false;
        this.connectorValid = false;
        this.relativityConnectorValid = false;
        this.restService.post(Endpoint.connectorManager_getReviewConnectors, { systemScope: true }).then(response => {
            this.isBusy = false;
            if (response?.reviewConnectors) {
                response.reviewConnectors.forEach(connector => {
                    if (connector?.reviewSystem === ReviewSystem.REEF_REVIEW) {
                        this.viewModel = new ReefReviewConnectorViewModel({
                            hostUrl: connector.hostURL,
                            remoteHost: connector.remoteHost,
                            remotePath: connector.remotePath
                        })
                        this.handle = connector.handle;
                        this.isEdit = true;
                    } 
                    if (connector?.reviewSystem === ReviewSystem.RELATIVITY) {
                        this.relativityHostUrl = connector.hostURL;
                        this.relativityHandle = connector.handle;
                        this.isEditRelativity = true;
                    }
                });
            } else {
                this.viewModel = new ReefReviewConnectorViewModel({ hostUrl: '', remoteHost: '', remotePath: '' })
                this.isEdit = false;
                this.isEditRelativity = false;
            }
        }, () => {
            this.isBusy = false;
            this.isEdit = false;
            this.isEditRelativity = false;
            this.viewModel = new ReefReviewConnectorViewModel({ hostUrl: '', remoteHost: '', remotePath: '' })
        });
    }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string, settingsType?: SettingsType): void {
        this.helpIndex = HelpIndex.sys_set_review_system;
        this.loadSettingsData();
    }

    openHelp = () => this.commonService.openHelp(this.helpIndex);

    saveChanges(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            const requestInfoReview: any = {
                hostURL: this.viewModel.model.hostUrl,
                remoteHost: this.viewModel.model.remoteHost,
                remotePath: this.viewModel.model.remotePath,
                reviewSystem: ReviewSystem.REEF_REVIEW
            };

            const requestInfoRelativity : any = {
                hostURL: this.relativityHostUrl,
                reviewSystem: ReviewSystem.RELATIVITY
            }

            const endpoint = (this.isEdit || this.isEditRelativity) ? Endpoint.connectorManager_updateReviewConnectors : Endpoint.connectorManager_createReviewConnectors;
            if (this.isEdit) {
                requestInfoReview.handle = this.handle;
            }
            if (this.isEditRelativity) {
                requestInfoRelativity.handle = this.relativityHandle;
            }
            this.isBusyMessage = 'Saving Review Connector Configuration ...';
            this.isBusy = true;

            this.restService.post(endpoint, {reviewConnectors: [requestInfoReview, requestInfoRelativity], systemScope: true}).then(response => {
                if (!this.commonService.reefReviewEnabled) {
                    this.commonService.reefReviewEnabled = true;
                };
                this.resetAndLoad();
                resolve(true);
            }, error => {
                this.isBusy = false;
                resolve(false);
            });
        });
    }

    resetAndLoad(): void {
        this.isBusy = false;
        this.isDirty = false;
        this.resetState();
        this.loadSettingsData();
    }

    resetState(): void {
        this.remoteHost.invalid = this.remoteHost.invalidIP = false;
        this.hostUrl.invalid = this.hostUrl.invalidURL = false;
        this.remotePath.invalid = this.remotePath.invalidPath = false;
        this.relativityUrl.invalid = this.relativityUrl.invalidURL = false;
    }

    onChange() {
        this.isDirty = this.viewModel.isDirty();
        this.connectorValid = false;
    }

    onChangeRelativityUrl() {
        this.isDirty = true;
        this.relativityConnectorValid = false;
    }

    discardChanges(): void {
        this.isDirty = false;
        this.relativityHostUrl = '';
        this.loadSettingsData();
        this.resetState();
    }

    isSaveDisabled(): boolean {
        let isEnabled = false;
        if (!this.isBusy) {
            if ((this.viewModel.model.remoteHost !== '' && !this.remoteHost.invalidIP) && (this.viewModel.model.hostUrl !== '' && !this.hostUrl.invalidURL) 
                && (this.viewModel.model.remotePath !== '' && !this.remotePath.invalidPath) || (this.relativityHostUrl !== '' && !this.relativityUrl.invalidURL)) {
                isEnabled = true;
            }
        }
        return !isEnabled;
    }

    isDiscardDisabled(): boolean {
        let isEnabled = false;
        if (!this.isBusy) {
            if (this.isDirty) {
                isEnabled = true;
            }
        }
        return !isEnabled;
    }

    validateIP(event: any): boolean {
        if (this.isIP(event) || this.isHostname(event)) {
            this.remoteHost.invalid = this.remoteHost.invalidIP = false;
            return (true);
        }
        this.remoteHost.errorMessage = event?.srcElement?.value === '' ? 'This field cannot be blank' : 'Invalid format';
        this.remoteHost.invalid = this.remoteHost.invalidIP = true;
        return (false);
    }

    validatePath(event: any): boolean {
        if (this.isPath(event)) {
            this.remotePath.invalid = this.remotePath.invalidPath = false;
            return (true);
        }
        this.remotePath.errorMessage = event?.srcElement?.value === '' ? 'This field cannot be blank' : 'Invalid format';
        this.remotePath.invalid = this.remotePath.invalidPath = true;
        return (false);
    }

    validateUrl(event: any, isRelativityUrl = false): boolean {
        if (this.isUrl(event)) {
            if (isRelativityUrl) {
                this.relativityUrl.invalid = this.relativityUrl.invalidURL = false;
            } else {
                this.hostUrl.invalid = this.hostUrl.invalidURL = false;
            }
            return (true);
        }
        if (isRelativityUrl) {
            this.relativityUrl.errorMessage = event?.srcElement?.value === '' ? 'This field cannot be blank' : 'Invalid format';
            this.relativityUrl.invalid = this.relativityUrl.invalidURL = true;
        } else {
            this.hostUrl.errorMessage = event?.srcElement?.value === '' ? 'This field cannot be blank' : 'Invalid format';
            this.hostUrl.invalid = this.hostUrl.invalidURL = true;
        }
        return (false);
    }

    isIP(event): boolean {
        return (/^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/.test(event.srcElement.value))
    }
    isHostname(event): boolean {
        return (/^(\\)?(([a-zA-Z]|[a-zA-Z][a-zA-Z0-9\-]*[a-zA-Z0-9])\.)*([A-Za-z]|[A-Za-z][A-Za-z0-9\-]*[A-Za-z0-9])$/.test(event.srcElement.value))
    }

    isPath(event): boolean {
        return (/^(\/{0,1}(?!\/))[A-Za-z0-9\/\-_]+(\.([a-zA-Z]+))*$/.test(event.srcElement.value));
    }

    isUrl(event): boolean {
        return (/^(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&=]*)(?:\/\S*)?$/.test(event.srcElement.value))
    }

    isReefReviewValidateEnabled() : boolean {
        if ((this.viewModel.model.remoteHost !== '' && !this.remoteHost.invalidIP) && (this.viewModel.model.hostUrl !== '' && !this.hostUrl.invalidURL) && (this.viewModel.model.remotePath !== '' && !this.remotePath.invalidPath)) {
            return true;
        }
        return false;
    }

    validateConnector(isRelativity = false) {
        const reviewSystemName = isRelativity ? 'Relativity' : 'Reef Review';
        this.isBusyMessage = `Validating ${reviewSystemName} Connector...`;
        const request = {
            hostURL: this.viewModel.model.hostUrl,
            remoteHost: this.viewModel.model.remoteHost,
            remotePath: this.viewModel.model.remotePath,
            reviewSystem: ReviewSystem.REEF_REVIEW
        },
            requestRelativity = { hostURL: this.relativityHostUrl, reviewSystem: ReviewSystem.RELATIVITY },
            validationModalTitle = `Validate ${reviewSystemName} Connector Configuration`,
            validationErrorTitle = `${reviewSystemName} Connector Configuration is not valid.`,
            validationSuccessTitle = `${reviewSystemName} Connector Configuration is valid.`;

        this.isBusy = true;
        this.restService.post(Endpoint.connectorManager_validateReviewConnector, isRelativity ? { connector: requestRelativity, systemScope: true } : { connector: request, systemScope: true }, false).then(response => {
            this.isBusy = false;
            if (response?.valid === false && response?.extendedStatus) {
                this.commonService.openErrorModal(validationErrorTitle, validationModalTitle);
                if (isRelativity) {
                    this.relativityConnectorValid = false;
                } else {
                    this.connectorValid = false;
                }
            } else if (response?.status === 'SUCCESS') {
                this.commonService.openSuccessToastNotification(validationSuccessTitle);
                if (isRelativity) {
                    this.relativityConnectorValid = true;
                } else {
                    this.connectorValid = true;
                }
            }
        }, error => {
            this.isBusy = false;
            this.commonService.openErrorModal(validationErrorTitle, validationModalTitle);
            if (isRelativity) {
                this.relativityConnectorValid = false;
            } else {
                this.connectorValid = false;
            }
        });

    }
}
